/**
 * Free-tier dictation allowance, as the UI presents it.
 *
 * The database is the authority (see record_voice_usage in 0024). These helpers
 * exist so the mic can gray out BEFORE someone records into a wall, and so the
 * remaining time reads the same everywhere.
 */

export interface QuotaStatus {
  tier: 'free' | 'pro';
  used_seconds: number;
  /** null for pro — unlimited. */
  limit_seconds: number | null;
  remaining_seconds: number | null;
  allowed: boolean;
}

/** Whether the mic should be usable at all. */
export function canDictate(q: QuotaStatus | null): boolean {
  // Unknown quota fails CLOSED. The server would refuse anyway, and a mic that
  // records and then throws the audio away is worse than one that's disabled.
  if (!q) return false;
  return q.allowed;
}

/** Human-readable remaining time, or null when unlimited/unknown. */
export function formatRemaining(q: QuotaStatus | null): string | null {
  if (!q || q.tier === 'pro' || q.remaining_seconds == null) return null;
  const s = Math.max(0, q.remaining_seconds);
  if (s <= 0) return 'none left';
  if (s < 60) return `${s}s left`;
  const mins = Math.floor(s / 60);
  return `${mins} min left`;
}

/**
 * Warn when the allowance is nearly gone, so the limit isn't a surprise.
 * Five minutes is roughly one long note — enough to finish the thought.
 */
export const LOW_REMAINING_SECONDS = 300;

export function isRunningLow(q: QuotaStatus | null): boolean {
  if (!q || q.tier === 'pro' || q.remaining_seconds == null) return false;
  return q.remaining_seconds > 0 && q.remaining_seconds <= LOW_REMAINING_SECONDS;
}

/** Message for the disabled mic / upgrade prompt, or null when fine. */
export function quotaMessage(q: QuotaStatus | null): string | null {
  if (!q) return 'Dictation is unavailable right now.';
  if (q.tier === 'pro') return null;
  if (!q.allowed) {
    return 'You have used all 60 minutes of free dictation. Upgrade to keep using it.';
  }
  if (isRunningLow(q)) {
    return `${formatRemaining(q)} of free dictation.`;
  }
  return null;
}

/** Fraction of the allowance consumed, 0–1. Null when unlimited. */
export function usedFraction(q: QuotaStatus | null): number | null {
  if (!q || q.tier === 'pro' || !q.limit_seconds) return null;
  return Math.min(1, Math.max(0, q.used_seconds / q.limit_seconds));
}
